/**
 * NewImportSheet — Tier 0 module-local "New import" 5-step modal.
 *
 * Per `.design-incoming/admin-20260522/IMPORTS_SPEC.md §6`.
 * Eleventh subtask of the SCRUM-15 admin epic (SCRUM-126).
 * Mounted by `ImportsQueuePage` when the `+ New import` button
 * is clicked. Permission gating happens at the trigger — the
 * sheet itself trusts its callers.
 *
 * ## Five steps
 *
 *   1. **Pick company** — typeahead "Search by name or slug."
 *      The component accepts a `companyOptions` prop (defaults
 *      to `[]`) — the Phase-1 stub set; once the BE ships a
 *      company-index endpoint the consumer passes the loaded
 *      list. Free-text input is preserved as a fallback so the
 *      sheet stays usable even without a loaded option list.
 *   2. **Pick type** — 4 radio cards (Properties / Reservations
 *      / Owners / Owner statements) each with description +
 *      "Download template" link (Phase-1 placeholder).
 *   3. **Upload file** — drag-and-drop OR click-to-upload.
 *      Accepts CSV / XLSX, max 20 MB. Inline validation rejects
 *      oversize files + wrong-extension files immediately.
 *   4. **Validate** — Phase-1 stub renders a fake row-count /
 *      column-mapping / detected-issues block. Real client +
 *      server validation lands when the BE arrives.
 *   5. **Queue** — primary button "Queue import" submits the
 *      stub: closes the sheet + the consumer routes to
 *      `/admin/imports?created=<stub-id>` so the list page can
 *      highlight the new row once the BE wiring lands.
 *
 * ## Layout
 *
 *   - Modal backdrop (`role="dialog" aria-modal="true"`).
 *   - Step indicator at top ("Step N of 5" + current step label).
 *   - Step content in the middle.
 *   - Footer: Back · Cancel · Continue (becomes "Queue import"
 *     on step 5).
 *   - Esc closes (calls `onCancel`).
 *
 * ## Phase-1 honesty
 *
 *   - File upload happens client-side only — the File object
 *     never leaves the browser. No real BE PUT.
 *   - Step 4 validation is a stub (fake row count derived from
 *     file size; no real CSV parse).
 *   - Step 5 "Queue import" doesn't fire a BE mutation — the
 *     consumer's `onSubmit` callback receives the form payload
 *     and decides what to do (in `ImportsQueuePage` that's a
 *     `navigate('?created=<stub-id>')`).
 *
 * Each of these stubs is documented inline; the full BE wire-up
 * is a follow-up ticket.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import {
  type ImportType,
  importTypeLabel,
} from './ImportTypeChip.js';

// ── Limits + helpers ───────────────────────────────────────────

/** Spec §6.3: "CSV / XLSX. Max 20 MB." */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'] as const;

/**
 * Exported pure helper — checks file size + extension. Returns
 * a human error message (rendered inline in step 3) or null
 * when the file is acceptable.
 */
export function validateUploadFile(file: File | null): string | null {
  if (file === null) return 'Pick a CSV or XLSX file (≤ 20 MB).';
  const name = file.name.toLowerCase();
  const okExt = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!okExt) {
    return 'Unsupported file type. Use CSV or XLSX.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File is too large (${formatMb(file.size)}). Max 20 MB.`;
  }
  return null;
}

function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

// ── Types ──────────────────────────────────────────────────────

export interface NewImportCompanyOption {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

export interface NewImportSubmitPayload {
  readonly companyId: string | null;
  readonly companyName: string;
  readonly type: ImportType;
  readonly fileName: string;
  readonly fileSizeBytes: number;
}

// ── Step model ────────────────────────────────────────────────

const STEP_LABELS = [
  'Company',
  'Type',
  'Upload',
  'Validate',
  'Queue',
] as const;

type StepIndex = 0 | 1 | 2 | 3 | 4;

// ── Type-card options ─────────────────────────────────────────

interface TypeOption {
  readonly id: ImportType;
  readonly desc: string;
}

const TYPE_OPTIONS: ReadonlyArray<TypeOption> = [
  {
    id: 'properties',
    desc: 'Property records — bedrooms, addresses, amenities, photos.',
  },
  {
    id: 'reservations',
    desc: 'Past and future reservations — guest, dates, totals.',
  },
  {
    id: 'owners',
    desc: 'Owner contacts — entity, address, payout account.',
  },
  {
    id: 'owner_statements',
    desc: 'Owner statements — monthly payouts and adjustments.',
  },
];

// ── Component entry ───────────────────────────────────────────

export interface NewImportSheetProps {
  /** Sheet open/close binding controlled by the consumer. */
  readonly open: boolean;
  /**
   * Called when the user dismisses the sheet. Fires on all three
   * close paths: the Cancel button, Esc, and a backdrop click
   * (a click directly on the dimmed overlay outside the panel).
   * Clicks inside the panel never close the sheet.
   */
  readonly onCancel: () => void;
  /**
   * Called with the validated payload when the user clicks
   * "Queue import" on step 5. The consumer decides what to do
   * (Phase 1: navigate to `/admin/imports?created=<stub-id>`;
   * Phase 2: TanStack mutation against `POST /admin/imports`).
   */
  readonly onSubmit: (payload: NewImportSubmitPayload) => void;
  /**
   * Phase-1 stub set for the company typeahead. Defaults to `[]`.
   * Once the BE ships a company-index endpoint the consumer
   * passes the loaded list.
   */
  readonly companyOptions?: ReadonlyArray<NewImportCompanyOption>;
}

export function NewImportSheet({
  open,
  onCancel,
  onSubmit,
  companyOptions = [],
}: NewImportSheetProps): React.ReactElement | null {
  const [step, setStep] = React.useState<StepIndex>(0);
  const [companyQuery, setCompanyQuery] = React.useState('');
  const [companyId, setCompanyId] = React.useState<string | null>(null);
  const [type, setType] = React.useState<ImportType | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);

  const headingId = 'new-import-sheet-heading';
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  // SCRUM-15 reviewer fix #3 (2026-05-27): aria-modal="true" promises a
  // focus trap that the round-1 implementation didn't deliver — keyboard
  // users could Tab behind the sheet, no initial focus moved into the
  // dialog on open, and focus didn't return to the opener on close.
  // This effect closes all three gaps:
  //
  //   1. Capture the previously focused element on open (`previousActive`)
  //      so we can restore it on close.
  //   2. Move initial focus into the panel — first focusable element.
  //      Scheduled via `requestAnimationFrame` so React has committed
  //      the panel to the DOM before we query for focusables.
  //   3. Restore focus to `previousActive` on close.
  React.useEffect(() => {
    if (!open) return undefined;
    const previousActive =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const rafId = requestAnimationFrame(() => {
      const focusables = getFocusableElements(panelRef.current);
      const initial = focusables[0];
      if (initial !== undefined) initial.focus();
    });
    return () => {
      cancelAnimationFrame(rafId);
      // Restore focus to the opener. Skip if the element has been
      // detached or no longer accepts focus (e.g. component unmounted).
      if (previousActive !== null && document.contains(previousActive)) {
        previousActive.focus();
      }
    };
  }, [open]);

  // Keyboard handlers — Escape closes, Tab cycles focus within the panel
  // (focus trap). Both consolidated here so the previously separate Esc
  // effect doesn't re-attach `keydown` listeners on every render of the
  // sheet's parents.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab' || panelRef.current === null) return;
      const focusables = getFocusableElements(panelRef.current);
      if (focusables.length === 0) {
        // Defensive: nothing to focus inside the panel. Swallow the Tab
        // so focus can't escape into the page behind the modal.
        event.preventDefault();
        return;
      }
      // `noUncheckedIndexedAccess` widens `focusables[i]` to `T | undefined`
      // even though we just length-checked > 0 — narrow here so the
      // downstream `first.focus()` / `last.focus()` calls typecheck.
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) return;
      const active =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const activeInsidePanel =
        active !== null && panelRef.current.contains(active);
      if (!activeInsidePanel) {
        // Focus left the panel somehow — pull it back to the first
        // focusable on the next Tab regardless of shift state.
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  // Reset state every time the sheet re-opens so a previous draft
  // doesn't bleed into the next "New import" attempt.
  React.useEffect(() => {
    if (!open) {
      setStep(0);
      setCompanyQuery('');
      setCompanyId(null);
      setType(null);
      setFile(null);
      setFileError(null);
    }
  }, [open]);

  if (!open) return null;

  const matchedCompanies = matchCompanyOptions(companyOptions, companyQuery);
  const companyOk =
    companyId !== null || companyQuery.trim().length >= 2;
  const typeOk = type !== null;
  const fileOk = file !== null && fileError === null;

  const canAdvance: Record<StepIndex, boolean> = {
    0: companyOk,
    1: typeOk,
    2: fileOk,
    3: true, // Validate is a stub — always can confirm
    4: true,
  };

  const advance = (): void => {
    if (!canAdvance[step]) return;
    if (step === 4) {
      handleSubmit();
      return;
    }
    setStep((s) => Math.min(4, (s + 1) as StepIndex) as StepIndex);
  };

  const back = (): void => {
    setStep((s) => Math.max(0, s - 1) as StepIndex);
  };

  const handleFile = (next: File | null): void => {
    const err = validateUploadFile(next);
    setFile(err === null ? next : null);
    setFileError(err);
  };

  const handleSubmit = (): void => {
    if (type === null || file === null) return;
    onSubmit({
      companyId,
      companyName:
        companyId !== null && companyOptions.length > 0
          ? companyOptions.find((c) => c.id === companyId)?.name ?? companyQuery
          : companyQuery,
      type,
      fileName: file.name,
      fileSizeBytes: file.size,
    });
  };

  // Backdrop dismissal: a click whose target IS the backdrop
  // wrapper (not a bubbled click from inside the panel) closes
  // the sheet. Comparing target === currentTarget is cleaner
  // than stopPropagation on the panel — a click anywhere inside
  // the panel has a deeper target, so it never matches.
  const handleBackdropClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      data-testid="new-import-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: 'color-mix(in oklab, #000000 50%, transparent)',
        padding: '24px',
      }}
    >
      <div
        ref={panelRef}
        data-testid="new-import-sheet-panel"
        className="flex w-full flex-col"
        style={{
          maxWidth: '560px',
          maxHeight: '90vh',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border-default)',
          overflow: 'hidden',
        }}
      >
        {/* Step indicator */}
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <p
            data-testid="new-import-sheet-step-counter"
            className="m-0 font-medium"
            style={{
              fontSize: '12px',
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              marginBottom: '4px',
            }}
          >
            Step {step + 1} of {STEP_LABELS.length} · {STEP_LABELS[step]}
          </p>
          <h2
            id={headingId}
            className="m-0 font-bold"
            style={{
              fontSize: '18px',
              color: 'var(--fg-primary)',
            }}
          >
            New import
          </h2>
          {/*
            Polite live-region per `ACCESSIBILITY.md`: wizard step
            advance announces "Step {n} of 5: {step name}" to
            screen readers. Visually hidden (the sighted cue is the
            visible counter above); the `role="status"` +
            `aria-live="polite"` text content updates on every step
            change so an SR user hears the transition. The
            `sr-only` class keeps it out of the visual layout while
            leaving it in the accessibility tree.
          */}
          <div
            data-testid="new-import-sheet-live-region"
            role="status"
            aria-live="polite"
            className="sr-only"
          >
            Step {step + 1} of {STEP_LABELS.length}: {STEP_LABELS[step]}
          </div>
        </header>

        {/* Step content */}
        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
          }}
        >
          {step === 0 ? (
            <StepCompany
              query={companyQuery}
              onQueryChange={(next) => {
                setCompanyQuery(next);
                setCompanyId(null);
              }}
              selectedId={companyId}
              onSelectId={(id) => {
                setCompanyId(id);
                const match = companyOptions.find((c) => c.id === id);
                if (match !== undefined) setCompanyQuery(match.name);
              }}
              matches={matchedCompanies}
            />
          ) : step === 1 ? (
            <StepType type={type} onSelect={(t) => setType(t)} />
          ) : step === 2 ? (
            <StepUpload
              file={file}
              error={fileError}
              onFile={handleFile}
            />
          ) : step === 3 ? (
            <StepValidate file={file} type={type} />
          ) : (
            <StepQueue
              file={file}
              type={type}
              companyName={companyQuery}
            />
          )}
        </div>

        {/* Footer */}
        <footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border-default)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="button"
            variant="secondary"
            disabled={step === 0}
            onClick={back}
          >
            ← Back
          </Button>
          <div className="flex items-center" style={{ gap: '8px' }}>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!canAdvance[step]}
              aria-disabled={!canAdvance[step]}
              onClick={advance}
            >
              {step === 4 ? 'Queue import' : 'Continue →'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ── Focus-trap helper ─────────────────────────────────────────

/**
 * Returns the focusable descendants of `container`, in document order.
 * Used by `<NewImportSheet>`'s focus trap. The selector matches the
 * standard "tab-stop" set (buttons, inputs, links, anything with a
 * positive `tabindex`); disabled and `tabindex="-1"` elements are
 * excluded. jsdom doesn't compute layout, so visibility filtering
 * (`offsetParent`, `getBoundingClientRect`) is skipped — every match
 * inside `aria-modal` panel content is considered tabbable.
 */
function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (container === null) return [];
  const selector = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

// ── Step 1: company typeahead ─────────────────────────────────

function matchCompanyOptions(
  options: ReadonlyArray<NewImportCompanyOption>,
  rawQuery: string,
): ReadonlyArray<NewImportCompanyOption> {
  const needle = rawQuery.trim().toLowerCase();
  if (needle.length < 2) return [];
  return options.filter((opt) => {
    return (
      opt.name.toLowerCase().includes(needle) ||
      opt.slug.toLowerCase().includes(needle)
    );
  });
}

function StepCompany({
  query,
  onQueryChange,
  selectedId,
  onSelectId,
  matches,
}: {
  readonly query: string;
  readonly onQueryChange: (next: string) => void;
  readonly selectedId: string | null;
  readonly onSelectId: (id: string) => void;
  readonly matches: ReadonlyArray<NewImportCompanyOption>;
}): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ gap: '10px' }}>
      <label className="flex flex-col" style={{ gap: '6px' }}>
        <span
          className="font-semibold"
          style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
        >
          Search a company by name or slug
        </span>
        {/* SCRUM-126: hand-rolled `<input>` retained for the
            typeahead — the field drives a live results list rendered
            as a sibling below, and kit `<TextField>` owns its own
            label/hint subtree that would fight the combobox-style
            composition. Same kit-first carve-out protocol as the
            SCRUM-118 OrgStatusModal confirm input. */}
        {/* eslint-disable-next-line @sparta/no-hand-rolled-input */}
        <input
          aria-label="Search a company by name or slug"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="suncoast, atlas, …"
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-canvas)',
            color: 'var(--fg-primary)',
            fontSize: '14px',
          }}
        />
      </label>
      {matches.length > 0 ? (
        <ul
          data-testid="new-import-company-matches"
          className="m-0 flex list-none flex-col p-0"
          style={{ gap: '4px' }}
        >
          {matches.map((opt) => (
            <li key={opt.id}>
              {/*
                SCRUM-126: typeahead option pattern — kit Button
                isn't ideal for a flat list of "click to select"
                options. Same documented carve-out family as the
                role cards / filter chips / menu items.
                eslint-disable-next-line @sparta/no-hand-rolled-button
              */}
              {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
              <button
                type="button"
                onClick={() => onSelectId(opt.id)}
                aria-pressed={selectedId === opt.id}
                className="w-full cursor-pointer text-left"
                style={{
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid',
                  borderColor:
                    selectedId === opt.id
                      ? 'var(--brand-500)'
                      : 'var(--border-default)',
                  background:
                    selectedId === opt.id
                      ? 'color-mix(in oklab, var(--brand-500) 10%, var(--bg-surface))'
                      : 'var(--bg-surface)',
                  fontSize: '13px',
                  color: 'var(--fg-primary)',
                }}
              >
                <span className="font-medium">{opt.name}</span>{' '}
                <span style={{ color: 'var(--fg-tertiary)' }}>
                  /{opt.slug}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : query.trim().length >= 2 ? (
        <p
          className="m-0"
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          No companies match. The BE company-index endpoint hasn't
          shipped yet — you can still continue with the typed name.
        </p>
      ) : (
        <p
          className="m-0"
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          Type at least 2 characters to search.
        </p>
      )}
    </div>
  );
}

// ── Step 2: type cards ────────────────────────────────────────

function StepType({
  type,
  onSelect,
}: {
  readonly type: ImportType | null;
  readonly onSelect: (next: ImportType) => void;
}): React.ReactElement {
  /**
   * WAI-ARIA radiogroup roving focus + arrow-key navigation per
   * `ACCESSIBILITY.md`. Exactly one card is in the tab order at
   * a time (the selected one, or the first if none selected);
   * arrow keys move selection + focus together. Same shape as
   * the SCRUM-124 Add-staff role cards.
   */
  const radioRefs = React.useRef<
    Map<ImportType, HTMLButtonElement | null>
  >(new Map());

  // `TYPE_OPTIONS` is a non-empty const tuple; narrow explicitly
  // to avoid the banned non-null assertion.
  const firstType = TYPE_OPTIONS[0];
  if (firstType === undefined) {
    throw new Error('TYPE_OPTIONS must contain at least one entry');
  }
  const focusedType: ImportType = type ?? firstType.id;

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    const currentIdx =
      type === null
        ? 0
        : TYPE_OPTIONS.findIndex((opt) => opt.id === type);
    let nextIdx = currentIdx;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % TYPE_OPTIONS.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIdx =
        (currentIdx - 1 + TYPE_OPTIONS.length) % TYPE_OPTIONS.length;
    } else if (event.key === 'Home') {
      nextIdx = 0;
    } else if (event.key === 'End') {
      nextIdx = TYPE_OPTIONS.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    const nextOption = TYPE_OPTIONS[nextIdx];
    if (nextOption === undefined) return;
    onSelect(nextOption.id);
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        radioRefs.current.get(nextOption.id)?.focus();
      });
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Import type"
      onKeyDown={handleKeyDown}
      className="flex flex-col"
      style={{ gap: '8px' }}
    >
      {TYPE_OPTIONS.map((opt) => {
        const checked = type === opt.id;
        return (
          <div
            key={opt.id}
            data-testid={`new-import-type-card-${opt.id}`}
            // `focus-within:ring-*` wraps the ENTIRE card in the
            // focus ring when its inner radio button receives
            // keyboard focus — per `ACCESSIBILITY.md`: "where the
            // card itself is the radio, the focus ring should wrap
            // the entire card." Mirrors the kit Button's
            // ring-2/ring-brand-500/ring-offset-2 focus treatment.
            className="flex flex-col focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2"
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-lg)',
              border: '1.5px solid',
              borderColor: checked
                ? 'var(--brand-500)'
                : 'var(--border-default)',
              background: checked
                ? 'color-mix(in oklab, var(--brand-500) 8%, var(--bg-surface))'
                : 'var(--bg-surface)',
              gap: '6px',
            }}
          >
            {/* SCRUM-126: radiogroup card — kit Button has no
                radiogroup variant. Same documented carve-out as
                SCRUM-124 role cards + SCRUM-120 wizard type cards.
                The "Download template" affordance lives OUTSIDE
                the radio button (sibling below) so we never nest
                interactive controls. The radio drops its own
                outline (`focus-visible:outline-none`) so the
                card-level `focus-within` ring is the single,
                full-card focus indicator. */}
            {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
            <button
              type="button"
              ref={(el) => {
                radioRefs.current.set(opt.id, el);
              }}
              role="radio"
              aria-checked={checked}
              aria-label={importTypeLabel(opt.id)}
              tabIndex={focusedType === opt.id ? 0 : -1}
              onClick={() => onSelect(opt.id)}
              className="w-full cursor-pointer border-0 bg-transparent p-0 text-left focus-visible:outline-none"
              style={{ color: 'inherit' }}
            >
              <span
                className="block font-semibold"
                style={{
                  fontSize: '14px',
                  color: checked ? 'var(--brand-700)' : 'var(--fg-primary)',
                  marginBottom: '4px',
                }}
              >
                {importTypeLabel(opt.id)}
              </span>
              <span
                className="block"
                style={{ fontSize: '12px', color: 'var(--fg-secondary)' }}
              >
                {opt.desc}
              </span>
            </button>
            {/*
              Spec §6.2: each type card has a "Download template"
              link. Phase-1: the BE template files haven't shipped,
              so this is a disabled placeholder affordance — visible
              + testable, but explicitly non-functional. A future
              ticket points it at the real template CSV endpoint.
            */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled
              aria-label={`Download ${importTypeLabel(opt.id)} template (coming soon)`}
            >
              Download template
            </Button>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 3: file upload ───────────────────────────────────────

function StepUpload({
  file,
  error,
  onFile,
}: {
  readonly file: File | null;
  readonly error: string | null;
  readonly onFile: (next: File | null) => void;
}): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    const dropped = event.dataTransfer.files[0] ?? null;
    onFile(dropped);
  };
  return (
    <div className="flex flex-col" style={{ gap: '10px' }}>
      <div
        data-testid="new-import-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Drop a CSV or XLSX file here, or click to choose one"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className="cursor-pointer text-center"
        style={{
          padding: '32px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '2px dashed',
          borderColor: isDragging
            ? 'var(--brand-500)'
            : 'var(--border-default)',
          background: isDragging
            ? 'color-mix(in oklab, var(--brand-500) 6%, var(--bg-surface))'
            : 'var(--bg-sunken)',
          fontSize: '13px',
          color: 'var(--fg-secondary)',
        }}
      >
        {file === null ? (
          <>
            <p className="m-0 font-semibold" style={{ marginBottom: '4px' }}>
              Drop a CSV or XLSX file here
            </p>
            <p className="m-0" style={{ fontSize: '12px' }}>
              or click to choose. Max 20 MB.
            </p>
          </>
        ) : (
          <>
            <p
              className="m-0 font-semibold"
              style={{ color: 'var(--fg-primary)', marginBottom: '4px' }}
            >
              {file.name}
            </p>
            <p className="m-0" style={{ fontSize: '12px' }}>
              {formatMb(file.size)}
            </p>
          </>
        )}
      </div>
      {/* Hidden file input triggered by the visible dropzone above.
          `type="file"` isn't a text-like input, so the
          no-hand-rolled-input rule (which targets text-like fields
          kit TextField could replace) doesn't apply here. */}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx"
        aria-label="File upload (CSV or XLSX, max 20 MB)"
        style={{ display: 'none' }}
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
      {error !== null ? (
        <p
          role="alert"
          className="m-0"
          style={{ fontSize: '13px', color: 'var(--fg-danger)' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Step 4: validate ──────────────────────────────────────────

function StepValidate({
  file,
  type,
}: {
  readonly file: File | null;
  readonly type: ImportType | null;
}): React.ReactElement {
  // Phase 1 stub: fake row count derived from file size so the
  // surface looks real without parsing the file. Once the BE
  // ships, this gets replaced with a real validate-call result.
  const fakeRowCount =
    file === null ? 0 : Math.max(1, Math.floor(file.size / 200));
  return (
    <div className="flex flex-col" style={{ gap: '10px' }}>
      <p
        className="m-0"
        style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
      >
        Pre-validation summary. Real client + server validation
        lands when the BE ships; for now this is a quick sanity
        check on the file you picked.
      </p>
      <ul
        className="m-0 flex list-none flex-col p-0"
        style={{
          gap: '6px',
          background: 'var(--bg-sunken)',
          padding: '12px',
          borderRadius: 'var(--radius-md)',
          fontSize: '13px',
        }}
      >
        <li>
          <strong>Type:</strong>{' '}
          {type !== null ? importTypeLabel(type) : '—'}
        </li>
        <li>
          <strong>File:</strong>{' '}
          {file !== null ? file.name : '—'}
        </li>
        <li>
          <strong>Estimated rows:</strong> ~{fakeRowCount.toLocaleString()}
        </li>
        <li>
          <strong>Detected issues:</strong> 0 (pre-BE stub)
        </li>
      </ul>
    </div>
  );
}

// ── Step 5: queue confirm ─────────────────────────────────────

function StepQueue({
  file,
  type,
  companyName,
}: {
  readonly file: File | null;
  readonly type: ImportType | null;
  readonly companyName: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ gap: '10px' }}>
      <p
        className="m-0"
        style={{
          fontSize: '14px',
          color: 'var(--fg-primary)',
          marginBottom: '4px',
        }}
      >
        Ready to queue
      </p>
      <p
        className="m-0"
        style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
      >
        We'll queue this import. You'll land back on the Imports
        list — the new row appears at the top with status{' '}
        <strong>queued</strong>. Running it begins in queue order.
      </p>
      <dl
        className="m-0 grid"
        style={{
          gridTemplateColumns: 'auto 1fr',
          gap: '6px 12px',
          fontSize: '13px',
          marginTop: '8px',
        }}
      >
        <dt
          className="font-medium"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Company
        </dt>
        <dd className="m-0" style={{ color: 'var(--fg-primary)' }}>
          {companyName}
        </dd>
        <dt
          className="font-medium"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Type
        </dt>
        <dd className="m-0" style={{ color: 'var(--fg-primary)' }}>
          {type !== null ? importTypeLabel(type) : '—'}
        </dd>
        <dt
          className="font-medium"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          File
        </dt>
        <dd className="m-0" style={{ color: 'var(--fg-primary)' }}>
          {file !== null ? file.name : '—'}
        </dd>
      </dl>
    </div>
  );
}
